import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyDevicePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-device`, { token });
        // Auto-login the user
        if (res.data.access_token) {
          localStorage.setItem("gmp_token", res.data.access_token);
          setToken(res.data.access_token);
          setUser(res.data.user);
        }
        setStatus("success");
        setMessage(res.data.message || "Device verified successfully!");
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Verification failed. The link may have expired.");
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 gradient-mesh">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-elevated rounded-3xl overflow-hidden" data-testid="verify-device-card">
          <CardContent className="p-8 md:p-10 text-center">
            <Link to="/" className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-glow">
                <img src="/logo.png" alt="Great Messiah Publication" className="w-full h-full object-contain" />
              </div>
              <span className="font-heading text-2xl font-bold">Great Messiah</span>
            </Link>

            {status === "verifying" && (
              <div data-testid="verify-loading">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
                <h2 className="text-xl font-bold mb-2">Verifying Device...</h2>
                <p className="text-sm text-muted-foreground">Please wait while we verify your device.</p>
              </div>
            )}

            {status === "success" && (
              <div data-testid="verify-success">
                <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold text-emerald-700 mb-2">Device Verified!</h2>
                <p className="text-sm text-muted-foreground mb-6">{message}</p>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => navigate("/dashboard")} className="w-full rounded-xl bg-primary h-11" data-testid="go-to-dashboard">
                    Go to Dashboard
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/admin")} className="w-full rounded-xl h-11" data-testid="go-to-admin">
                    Go to Admin Panel
                  </Button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div data-testid="verify-error">
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-red-700 mb-2">Verification Failed</h2>
                <p className="text-sm text-muted-foreground mb-6">{message}</p>
                <div className="flex flex-col gap-2">
                  <Button asChild className="w-full rounded-xl bg-primary h-11">
                    <Link to="/login" data-testid="back-to-login">Try Logging In Again</Link>
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 justify-center text-xs text-muted-foreground">
              <Shield className="w-3.5 h-3.5" />
              <span>Multi-device security protection</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
